import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  CreditCard, AlertCircle, Receipt, CheckCircle, 
  ArrowRight, Building2, Info, FileText, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const GSTRechargeModal = ({ open, onClose, onSuccess, rechargeModule = 'legal_opinions' }) => {
  const navigate = useNavigate();
  const [credits, setCredits] = useState(200);
  const [loading, setLoading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [step, setStep] = useState('amount'); // 'amount', 'preview', 'success'
  const [billingProfile, setBillingProfile] = useState(null);
  const [gstPreview, setGstPreview] = useState(null);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setStep('amount');
      setGstPreview(null);
      setInvoiceResult(null);
      setError(null);
      fetchBillingProfile();
    }
  }, [open]);

  const fetchBillingProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/invoice/billing-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBillingProfile(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setBillingProfile(null);
      } else {
        console.error('Error fetching billing profile:', err);
      }
    }
  };

  const fetchGSTPreview = async () => {
    if (!billingProfile) {
      setError('Please set up your billing profile first');
      return;
    }
    
    setLoadingPreview(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/wallet/recharge/preview`,
        { credits: parseFloat(credits), module: rechargeModule },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setGstPreview(response.data);
      setStep('preview');
    } catch (err) {
      console.error('Error fetching GST preview:', err);
      setError(err.response?.data?.detail || 'Failed to calculate GST');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleRecharge = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      const rechargeCredits = parseFloat(credits);
      const amountInr = gstPreview?.taxable_amount || gstPreview?.base_amount || 0;
      const totalWithGst = gstPreview?.total_amount || amountInr;
      const amountPaise = Math.round(totalWithGst * 100); // Razorpay uses paise
      
      // Step 1: Create Razorpay order via invoice system
      const orderResponse = await axios.post(
        `${API}/api/invoice/create-payment-order`,
        {
          amount: amountInr,
          credits: rechargeCredits,
          purpose: 'wallet_recharge',
          module: rechargeModule
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      const { order_id, razorpay_key, amount: orderAmount, currency } = orderResponse.data;
      
      // Step 2: Open Razorpay checkout
      const options = {
        key: razorpay_key,
        amount: orderAmount,
        currency: currency || 'INR',
        name: 'TechOrange Legal',
        description: `Wallet Recharge - ${rechargeCredits} credits`,
        order_id: order_id,
        handler: async function (response) {
          // Step 3: Complete recharge after successful payment
          try {
            const rechargeResponse = await axios.post(
              `${API}/api/invoice/wallet-recharge`,
              { 
                amount: amountInr,
                credits: rechargeCredits,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              },
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            setInvoiceResult(rechargeResponse.data);
            setStep('success');
            toast.success('Payment successful! Wallet recharged.');
            setLoading(false);
            
            if (onSuccess) {
              onSuccess(rechargeResponse.data);
            }
          } catch (rechargeErr) {
            console.error('Recharge failed after payment:', rechargeErr);
            setError('Payment received but recharge failed. Please contact support.');
            setLoading(false);
          }
        },
        prefill: {
          name: billingProfile?.legal_name || '',
          email: billingProfile?.email || '',
          contact: billingProfile?.phone || ''
        },
        theme: {
          color: '#3B82F6'
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            toast.info('Payment cancelled');
          }
        }
      };
      
      // Load Razorpay script if not already loaded
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => {
          const rzp = new window.Razorpay(options);
          rzp.open();
        };
        document.body.appendChild(script);
      } else {
        const rzp = new window.Razorpay(options);
        rzp.open();
      }
      
    } catch (err) {
      console.error('Error creating order:', err);
      setError(err.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  const downloadInvoice = async () => {
    if (!invoiceResult?.invoice_id) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login again');
        return;
      }
      
      const response = await axios.get(
        `${API}/api/invoice/invoices/${invoiceResult.invoice_id}/download`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Check if response is actually a PDF
      if (response.data.type !== 'application/pdf') {
        throw new Error('Invalid response type');
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const filename = invoiceResult.invoice_number 
        ? `${invoiceResult.invoice_number.replace(/\//g, '_')}.pdf`
        : `invoice_${invoiceResult.invoice_id}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded!');
    } catch (err) {
      console.error('Error downloading invoice:', err);
      toast.error('Failed to download invoice. Please try from Invoice History page.');
    }
  };

  const formatCurrency = (value) => {
    return `₹${parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleClose = () => {
    if (step === 'success') {
      // Refresh wallet balance
      window.location.reload();
    }
    onClose();
  };

  // Render: No Billing Profile
  if (!billingProfile && step === 'amount') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Billing Profile Required
            </DialogTitle>
            <DialogDescription>
              Set up a billing profile before creating GST-compliant wallet recharge invoices.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-800">
                To generate GST-compliant invoices for wallet recharge, please set up your billing profile first.
              </p>
            </div>
            
            <div className="space-y-2 text-sm text-gray-600">
              <p>Your billing profile includes:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Business/Legal name</li>
                <li>Billing address</li>
                <li>State (for GST calculation)</li>
                <li>GSTIN (optional, for ITC)</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={() => navigate('/settings/billing-profile')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Set Up Billing Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Render: Amount Entry
  if (step === 'amount') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Recharge Wallet
            </DialogTitle>
            <DialogDescription>
              Enter the number of credits to purchase. GST is applied only during recharge.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Billing Profile Summary */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Billing To</p>
                  <p className="font-medium text-sm">{billingProfile.legal_name}</p>
                  <p className="text-xs text-gray-500">{billingProfile.state} ({billingProfile.state_code})</p>
                </div>
                <button
                  onClick={() => navigate('/settings/billing-profile')}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Edit
                </button>
              </div>
            </div>
            
            {/* Amount Input */}
            <div className="space-y-2">
              <Label htmlFor="amount">Recharge Credits</Label>
              <Input
                id="amount"
                type="number"
                min="1"
                max="10000000"
                step="1"
                value={credits}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || val === '0') {
                    setCredits('');
                  } else {
                    setCredits(parseInt(val) || '');
                  }
                }}
                onBlur={(e) => {
                  const val = parseInt(e.target.value) || 1;
                  setCredits(Math.max(1, val));
                }}
                className="text-lg font-semibold"
                data-testid="recharge-credits-input"
                placeholder="Enter credits"
              />
              <p className="text-xs text-gray-500">Enter credits to add. GST applies only at recharge.</p>
            </div>
            
            {/* Quick Credit Buttons */}
            <div className="flex flex-wrap gap-2">
              {[200, 500, 1000, 2500, 5000].map(val => (
                <button
                  key={val}
                  onClick={() => setCredits(val)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    credits === val 
                      ? 'bg-blue-100 border-blue-500 text-blue-700' 
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {val.toLocaleString()} credits
                </button>
              ))}
            </div>
            
            {/* GST Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-800">
                  GST will be added to your recharge amount. 
                  {billingProfile.gstin ? ' Since you have a GSTIN, you may claim Input Tax Credit.' : ''}
                </p>
              </div>
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={fetchGSTPreview}
              disabled={loadingPreview || credits < 1}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="preview-gst-button"
            >
              {loadingPreview ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  Preview GST
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Render: GST Preview
  if (step === 'preview' && gstPreview) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-green-600" />
              GST Breakdown
            </DialogTitle>
            <DialogDescription>
              Review the recharge credits, GST split, and payable amount before payment.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* GST Type Badge */}
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              gstPreview.gst_type === 'CGST_SGST' 
                ? 'bg-green-100 text-green-700' 
                : 'bg-purple-100 text-purple-700'
            }`}>
              {gstPreview.gst_type === 'CGST_SGST' ? 'Intra-State (CGST + SGST)' : 'Inter-State (IGST)'}
            </div>
            
            {/* Breakdown Table */}
            <div className="bg-gray-50 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="px-4 py-3 text-gray-600">Base Amount</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(gstPreview.base_amount)}</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-4 py-3 text-gray-600">Credits</td>
                    <td className="px-4 py-3 text-right font-medium">{gstPreview.wallet_credit}</td>
                  </tr>
                  
                  {gstPreview.cgst_amount > 0 && (
                    <tr className="border-b border-gray-200">
                      <td className="px-4 py-3 text-gray-600">CGST @ {gstPreview.cgst_rate}%</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(gstPreview.cgst_amount)}</td>
                    </tr>
                  )}
                  
                  {gstPreview.sgst_amount > 0 && (
                    <tr className="border-b border-gray-200">
                      <td className="px-4 py-3 text-gray-600">SGST @ {gstPreview.sgst_rate}%</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(gstPreview.sgst_amount)}</td>
                    </tr>
                  )}
                  
                  {gstPreview.igst_amount > 0 && (
                    <tr className="border-b border-gray-200">
                      <td className="px-4 py-3 text-gray-600">IGST @ {gstPreview.igst_rate}%</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(gstPreview.igst_amount)}</td>
                    </tr>
                  )}
                  
                  <tr className="bg-blue-50">
                    <td className="px-4 py-3 font-semibold text-gray-900">Total Payable</td>
                    <td className="px-4 py-3 text-right font-bold text-blue-600 text-lg">
                      {formatCurrency(gstPreview.total_amount)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            {/* Wallet Credit Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-green-800">Wallet Credit</span>
                <span className="font-bold text-green-700">{gstPreview.wallet_credit} credits</span>
              </div>
              <p className="text-xs text-green-600 mt-1">
                GST amount is not added to wallet balance
              </p>
            </div>
            
            {/* Place of Supply */}
            <div className="text-xs text-gray-500">
              Place of Supply: {gstPreview.place_of_supply}
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setStep('amount')}>
              Back
            </Button>
            <Button
              onClick={handleRecharge}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-recharge-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirm & Recharge
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Render: Success
  if (step === 'success' && invoiceResult) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              Recharge Successful!
            </DialogTitle>
            <DialogDescription>
              Your wallet has been credited and the invoice is ready to download.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Success Banner */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-700">
                {invoiceResult.wallet_credit} credits
              </p>
              <p className="text-sm text-green-600">added to your wallet</p>
            </div>
            
            {/* Invoice Info */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Invoice Number</span>
                <span className="font-mono font-medium">{invoiceResult.invoice_number}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Paid</span>
                <span className="font-medium">{formatCurrency(invoiceResult.total_paid)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">GST Amount</span>
                <span className="font-medium">{formatCurrency(invoiceResult.gst_amount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">New Balance</span>
                <span className="font-bold text-green-600">{invoiceResult.new_balance} credits</span>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={downloadInvoice}
              className="w-full sm:w-auto"
              data-testid="download-invoice-button"
            >
              <FileText className="w-4 h-4 mr-2" />
              Download Invoice
            </Button>
            <Button
              onClick={handleClose}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return null;
};

export default GSTRechargeModal;
